import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// PACKAGES
import { Form, Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';

// ICONS
import { ReactComponent as EyeOnIcon } from '../../../assets/svg/eye-on.svg';
import { ReactComponent as EyeOffIcon } from '../../../assets/svg/eye-off.svg';
import { ReactComponent as MailSentIcon } from '../../../assets/svg/mail-sent.svg';

// TYPES
import { AccountEmailChangeRequestCommand } from '../../../api/models';
import { closeModalType } from '../../../components/GlobalModal';

interface AccountEmailModalTypes {
	accountDetails: {
		childAwareId?: string;
		email?: string;
	};
	closeModal?: closeModalType;
}

const AccountEmailModal: React.FC<AccountEmailModalTypes> = ({ accountDetails, closeModal }) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [showPassword, setShowPassword] = React.useState<boolean>(false);

	const [isEmailSent, setIsEmailSent] = React.useState<boolean>(false);

	const [newlyCreatedEmail, setNewlyCreatedEmail] = React.useState<string>('');

	// INITIAL FORM VALUES
	const initialValues = {
		childawareId: accountDetails.childAwareId || '',
		email: accountDetails.email || '',
		currentPassword: ''
	};

	// VALIDATION
	const validationSchema = Yup.object({
		email: Yup.string().required('Required').email('Please enter a valid email address'),
		currentPassword: Yup.string()
			.required('Required')
			.min(12, 'Minimum of 12 characters')
			.matches(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{12,}$/, 'Please enter a valid password')
	});

	// SUBMIT
	const submitFormData = async (values: any) => {
		setIsLoading(true);

		const data: AccountEmailChangeRequestCommand = { newEmail: values.email, password: values.currentPassword };

		const response = await apibridge.postAccountEmailChangeRequest(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setNewlyCreatedEmail(values.email);

			setIsLoading(false);

			setIsEmailSent(true);
		}
	};

	React.useEffect(() => {}, [isEmailSent]);

	return (
		<div className="email-modal">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={submitFormData}>
								{(formik) => {
									return (
										<>
											{!isEmailSent ? (
												<Form translate="yes">
													<div className="form-group">
														<label htmlFor="childawareId">ChildAware ID</label>

														<Field name="childawareId" type="text" readOnly className="form-control disabled-input" />
													</div>

													<div className="form-group">
														<label htmlFor="email">New Email</label>

														<Field name="email" type="email" className="form-control" />

														<ErrorMessage component={'div'} className="error-message" name="email" />
													</div>

													<div className="form-group">
														<label htmlFor="currentPassword">Current password</label>

														<div className="password-wrapper">
															<Field
																name="currentPassword"
																id="currentPassword"
																type={showPassword ? 'text' : 'password'}
																className="form-control"
															/>

															<Button
																className="btn-text"
																buttonIcon={showPassword ? <EyeOffIcon /> : <EyeOnIcon />}
																onClick={() => setShowPassword(!showPassword)}
																type="button"
															></Button>
														</div>

														<ErrorMessage component={'div'} className="error-message" name="currentPassword" />
													</div>

													<Button disabled={isLoading} type="submit" className="btn-primary full-width">
														Send Confirmation Email
													</Button>
												</Form>
											) : (
												<>
													<MailSentIcon className="center-icon" />

													<p className="request-text">
														A confirmation email has been sent to your new email address.
														<br /> <strong>{newlyCreatedEmail}</strong>
													</p>

													<Button
														onClick={() => {
															if (closeModal) {
																closeModal();
															}
														}}
														type="button"
														className="btn btn-primary full-width mbtom-16"
													>
														Close
													</Button>
												</>
											)}
										</>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default AccountEmailModal;
